// Adapter: fact-based TimelineRecord[] → legacy TimelineEvent[].
//
// Phase 2 replaced the AI-enriched timeline with a deterministic, fact-based
// one. The dossier (PDF) and brief (DOCX) exports still build their
// chronological narrative through renderProseFromEvents(TimelineEvent[]). To
// keep those exports working — now with verified, no-AI content — we map each
// record + its facts onto the event shape the prose renderer expects. Every
// field is derived mechanically from record metadata or verified facts.

import type { TimelineEvent, TimelineRecord } from "../types";
import { isPlaceholderParty } from "./parties";

// source_type → TimelineEvent.type union. Anything without a direct match
// falls through to "other"; this only affects export node colour/labels.
function mapType(sourceType: string): TimelineEvent["type"] {
  switch (sourceType) {
    case "email":
      return "email";
    case "letter":
      return "letter";
    case "phone_call":
      return "phone";
    default:
      return "other";
  }
}

function mapDirection(direction: string): TimelineEvent["direction"] {
  return direction === "outbound" ? "outbound" : "inbound";
}

export function timelineRecordsToEvents(
  records: TimelineRecord[],
  // Optional party canonicalizer (Round 10, 1D): raw stored value →
  // canonical display name, from buildPartyIndex. Without it, values pass
  // through verbatim; placeholders are dropped from the aggregate list
  // either way.
  canonicalOf?: (raw: string) => string | null,
): TimelineEvent[] {
  const canon = (p: string) => canonicalOf?.(p) ?? p;
  return records.map((r) => {
    const amounts = r.facts
      .map((f) => f.amount)
      .filter((a): a is string => !!a);
    const parties = Array.from(
      new Set(
        [r.from, ...r.facts.map((f) => f.party)]
          .filter((p): p is string => !!p)
          .filter((p) => !isPlaceholderParty(p))
          .map(canon),
      ),
    );
    // The detail/summary is the record's verified facts, joined — no prose.
    const detail = r.facts.map((f) => f.summary).join(". ");
    return {
      record_id: r.id,
      date: r.date,
      title: r.subject ?? "(no subject)",
      detail,
      party: r.from ? canon(r.from) : "",
      direction: mapDirection(r.direction),
      significance: r.significance,
      type: mapType(r.type),
      amount: amounts[0] ?? null,
      summary: detail,
      parties,
      amounts,
      citations: [`[rec:${r.id}]`],
    };
  });
}
